'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export interface Pet {
  id: string;
  name: string;
  species: string;
  breed: string;
  age: string;
  size: string;
  gender: string;
  temperament: string[];
  location: string;
  distance: string;
  image: string;
  alt: string;
  shelterName: string;
  specialNeeds: boolean;
  vaccinated: boolean;
  spayedNeutered: boolean;
  compatibilityScore: number;
  quickFacts: string[];
}

interface PetCardProps {
  pet: Pet;
  onCompare: (petId: string) => void;
  isComparing: boolean;
}

const PetCard = ({ pet, onCompare, isComparing }: PetCardProps) => {
  const handleWhatsAppMessage = () => {
    // Prepare pet details message
    const petDetails = `Hi! I'm interested in adopting ${pet.name}

Pet Details:
- Name: ${pet.name}
- Breed: ${pet.breed}
- Age: ${pet.age}
- Size: ${pet.size}
- Gender: ${pet.gender}
- Location: ${pet.location}
- Shelter: ${pet.shelterName}
- Temperament: ${pet.temperament.join(', ')}
- Vaccinated: ${pet.vaccinated ? 'Yes' : 'No'}
- Spayed/Neutered: ${pet.spayedNeutered ? 'Yes' : 'No'}
- Compatibility Score: ${pet.compatibilityScore}%

I would like to learn more about this pet and the adoption process.`;

    // Copy to clipboard
    navigator.clipboard.writeText(petDetails).then(() => {
      console.log('Pet details copied to clipboard');
    }).catch((err) => {
      console.error('Failed to copy pet details:', err);
    });

    // Open WhatsApp with pre-filled message
    const phoneNumber = '12315649442'; // Remove special characters from +1 (231) 564-9442
    const encodedMessage = encodeURIComponent(petDetails);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Open in new tab
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden hover:shadow-elevated transition-all duration-300 group">
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={pet.image}
          alt={pet.alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 right-3 flex flex-col space-y-2">
          <button
            onClick={() => onCompare(pet.id)}
            className={`p-2 rounded-full shadow-md transition-colors ${isComparing ? 'bg-brand-cta text-white' : 'bg-white text-foreground hover:bg-muted'
              }`}
            aria-label={isComparing ? 'Remove from comparison' : 'Add to comparison'}
          >
            <Icon name="ScaleIcon" size={20} variant={isComparing ? 'solid' : 'outline'} />
          </button>
          <button
            className="p-2 bg-white rounded-full shadow-md hover:bg-muted transition-colors"
            aria-label="Save to favorites"
          >
            <Icon name="HeartIcon" size={20} variant="outline" />
          </button>
        </div>
        {pet.compatibilityScore >= 85 && (
          <div className="absolute top-3 left-3 px-3 py-1.5 bg-success text-white text-xs font-semibold rounded-full flex items-center space-x-1">
            <Icon name="SparklesIcon" size={16} variant="solid" />
            <span>Great Match</span>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-xl font-headline font-semibold text-foreground mb-1">{pet.name}</h3>
            <p className="text-sm text-muted-foreground">{pet.breed} • {pet.age}</p>
          </div>
          <div className="flex flex-col items-end">
            <div className="flex items-center space-x-1 mb-1">
              <Icon name="MapPinIcon" size={16} variant="outline" className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{pet.distance}</span>
            </div>
            <span className="text-xs text-muted-foreground">{pet.location}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {pet.temperament.slice(0, 3).map((trait) => (
            <span
              key={trait}
              className="px-2 py-1 bg-muted text-foreground text-xs font-medium rounded-full"
            >
              {trait}
            </span>
          ))}
        </div>

        <div className="flex items-center space-x-4 mb-3 text-xs text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name={pet.vaccinated ? 'CheckCircleIcon' : 'XCircleIcon'} size={16} variant="solid" className={pet.vaccinated ? 'text-success' : 'text-muted-foreground'} />
            <span>Vaccinated</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name={pet.spayedNeutered ? 'CheckCircleIcon' : 'XCircleIcon'} size={16} variant="solid" className={pet.spayedNeutered ? 'text-success' : 'text-muted-foreground'} />
            <span>Spayed/Neutered</span>
          </div>
        </div>

        <div className="border-t border-border pt-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-foreground">Compatibility Score</span>
            <span className="text-sm font-bold text-brand-cta">{pet.compatibilityScore}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand-cta to-accent transition-all duration-300"
              style={{ width: `${pet.compatibilityScore}%` }}
            />
          </div>
        </div>

        <div className="flex items-center space-x-2 mb-4">
          <Icon name="BuildingOfficeIcon" size={16} variant="outline" className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{pet.shelterName}</span>
        </div>

        <div className="flex space-x-2">
          <Link
            href={`/pet-profile/${pet.id}`}
            className="flex-1 px-4 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors text-center"
          >
            View Profile
          </Link>
          <button
            onClick={handleWhatsAppMessage}
            className="px-4 py-2.5 bg-brand-cta text-white font-semibold rounded-lg hover:bg-brand-cta/90 transition-colors flex items-center justify-center space-x-2"
            aria-label="Message on WhatsApp"
            title="Copy pet details and open WhatsApp chat"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} variant="outline" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PetCard;